import { ButtonProps, Text, useToast } from "@chakra-ui/react";
import { useCallback, useContext, useState } from "react";
import { MdOutlineSave } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton, RanaModal } from "@nens/ranalib";

import { ConflictDetails, useSavePublicationVersion } from "../../../api/save-version";
import { PublicationVersionFunctionsContext } from "../../../components/store-wrapper";

interface SaveChangesButtonProps extends ButtonProps {
  canSaveChanges: boolean;
  buttonText?: string;
  onSuccess?: (newVersion: number) => void;
}

export function SaveChangesButton({
  canSaveChanges,
  buttonText,
  onSuccess,
  ...rest
}: SaveChangesButtonProps) {
  const toast = useToast();
  const intl = useIntl();

  const [conflict, setConflict] = useState<ConflictDetails | null>(null);

  const { mutate: savePublication, isMutating } = useSavePublicationVersion(conflict, setConflict);

  const clickSaveButton = useCallback(
    (forceConflict = false) =>
      savePublication(forceConflict, {
        onSuccess: (newVersion) => {
          if (newVersion) {
            onSuccess?.(newVersion.version);
            toast({
              title: intl.formatMessage({
                id: "publication.save_success",
                defaultMessage: "Changes saved successfully",
              }),
              status: "success",
            });
          }
        },
        onError: () => {
          toast({
            title: intl.formatMessage({
              id: "publication.save_error",
              defaultMessage: "Something went wrong trying to save changes",
            }),
            status: "error",
          });
        },
      }),
    [savePublication, toast, intl, onSuccess]
  );

  return (
    <>
      <RanaButton
        variant="primary"
        isDisabled={!canSaveChanges || isMutating}
        title={
          !canSaveChanges
            ? intl.formatMessage({
                id: "publication.save.disabled.no_changes",
                defaultMessage: "No unsaved changes",
              })
            : undefined
        }
        onClick={() => clickSaveButton()}
        leftIcon={<MdOutlineSave />}
        {...rest}
      >
        {buttonText ?? (
          <FormattedMessage id="publication.save_to_draft" defaultMessage="Save to draft" />
        )}
      </RanaButton>
      <ResolveConflictModal
        conflict={conflict}
        setConflict={setConflict}
        clickSaveButton={clickSaveButton}
      />
    </>
  );
}

function ResolveConflictModal({
  conflict,
  setConflict,
  clickSaveButton,
}: {
  conflict: ConflictDetails | null;
  setConflict: (details: ConflictDetails | null) => void;
  clickSaveButton: (forceConflict: boolean) => void;
}) {
  const intl = useIntl();
  const { resetPublicationVersion } = useContext(PublicationVersionFunctionsContext);
  if (!conflict) return null;

  const onClose = () => setConflict(null);

  const timeSinceConflictCreated = Date.now() - new Date(conflict.conflictCreatedAt).getTime();
  const minutesSinceConflictCreated = Math.floor(timeSinceConflictCreated / 1000 / 60);
  const secondsSinceConflictCreated = Math.floor(timeSinceConflictCreated / 1000);

  const timeSinceConflictCreatedText =
    minutesSinceConflictCreated > 0
      ? intl.formatMessage(
          {
            id: "publication.save_conflict_time_minutes",
            defaultMessage: "{minutes} minute(s) ago",
          },
          { minutes: minutesSinceConflictCreated }
        )
      : intl.formatMessage(
          {
            id: "publication.save_conflict_time_seconds",
            defaultMessage: "{seconds} second(s) ago",
          },
          { seconds: secondsSinceConflictCreated }
        );

  return (
    <RanaModal
      isOpen={true}
      onClose={onClose}
      topBar={intl.formatMessage({
        id: "publication.save_conflict",
        defaultMessage: "Conflict detected",
      })}
      buttons={[
        {
          children: <FormattedMessage id="button.ignore" defaultMessage="Don't save changes" />,
          variant: "tertiary",
          onClick: onClose,
        },
        {
          children: (
            <FormattedMessage id="button.discard_changes" defaultMessage="Discard my changes" />
          ),
          variant: "danger",
          onClick: () => resetPublicationVersion(true),
        },
        {
          children: (
            <FormattedMessage
              id="button.force_overwrite"
              defaultMessage="Overwrite other version"
            />
          ),
          variant: "danger",
          onClick: () => clickSaveButton(true),
        },
      ]}
    >
      <Text>
        <FormattedMessage
          id="publication.save_conflict_message"
          defaultMessage="A new version of this publication was created since you last fetched it (by {author}, {time})."
          values={{ time: timeSinceConflictCreatedText, author: conflict.conflictCreatedByName }}
        />
      </Text>
      <Text>
        <FormattedMessage
          id="publication.save_conflict_what_to_do"
          defaultMessage="What would you like to do?"
        />
      </Text>
    </RanaModal>
  );
}
